/**
 * GreetingBlock - centered hero greeting cho /chat empty state.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §7.3 + AC-07.
 *
 * UPL-safe wording (legal-upl HARD VETO + cleared 2026-05-27 pre-screen):
 *   Headline (case-situation question, not "look up" lookup tone):
 *     VN guest:     "Bạn đang gặp tình huống pháp lý nào?"
 *     VN signed-in: "Chào [name], bạn đang gặp tình huống pháp lý nào?"
 *     EN guest:     "What legal situation are you facing?"
 *     EN signed-in: "Hi [name], what legal situation are you facing?"
 *   Subline (frames the retrieval-with-citation output, no advisory):
 *     VN: "Mô tả để Asklaw trích Điều/Khoản pháp luật áp dụng."
 *     EN: "Describe it and Asklaw will cite the applicable Articles."
 *
 * Wording rationale (founder reset 2026-05-27 - old "tra cứu pháp luật"
 * felt like keyword lookup, missed case-consultation core UX):
 *   - "tình huống pháp lý / legal situation" = observable user-described
 *     fact pattern, KHONG implying lawyer-client per Luật Luật sư 65/2006
 *     Đ.4 + 20/2012 Đ.22.
 *   - "trích Điều/Khoản pháp luật áp dụng / cite the applicable Articles"
 *     = retrieval action (same as Google Scholar legal), KHONG legal opinion.
 *   - Avoid: "tư vấn", "advise", "your case" (EN UPL trigger), "giải pháp".
 *   - Legal-upl pre-screen verdict D = PASS LOW (lowest risk + best UX fit).
 *
 * Layout:
 *   - AccentSparkle 32px centered above headline
 *   - Headline gradient text fill (1 of 3 allowed gradient spot per AC-09)
 *   - Subline body-color, smaller, max-width 560px
 *   - aria-live="polite" so screen reader announce when appearing
 *
 * Animation orchestrated externally via use-gsap-chat-orchestrate.ts.
 */

import { AccentSparkle } from './AccentSparkle';

export type GreetingBlockProps = {
  userName?: string | null;
  lang: 'vi' | 'en';
};

function firstName(name: string): string {
  // VN convention: last token is given name (vd "Nguyen Van Trung" -> "Trung")
  const trimmed = name.trim();
  if (!trimmed) return '';
  const tokens = trimmed.split(/\s+/);
  return tokens[tokens.length - 1];
}

export function GreetingBlock({ userName, lang }: GreetingBlockProps) {
  const display = userName ? firstName(userName) : null;

  let headline: string;
  let subline: string;
  if (lang === 'vi') {
    headline = display
      ? `Chào ${display}, bạn đang gặp tình huống pháp lý nào?`
      : 'Bạn đang gặp tình huống pháp lý nào?';
    subline = 'Mô tả để Asklaw trích Điều/Khoản pháp luật áp dụng.';
  } else {
    headline = display
      ? `Hi ${display}, what legal situation are you facing?`
      : 'What legal situation are you facing?';
    subline = 'Describe it and Asklaw will cite the applicable Articles.';
  }

  return (
    <div className="greeting-block" data-greeting-block>
      <div className="greeting-block__sparkle" data-greeting-sparkle>
        <AccentSparkle size={32} />
      </div>
      <h1
        className="greeting-headline"
        data-greeting-headline
        aria-live="polite"
      >
        {headline}
      </h1>
      <p className="greeting-subline" data-greeting-subline>
        {subline}
      </p>
    </div>
  );
}

export default GreetingBlock;
